import React from 'react'
import { Modal, Row, Col, Space, Typography, theme } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEarListen } from '@fortawesome/free-solid-svg-icons'
import { useSelector } from 'react-redux'
import useSimplifiedTranscribe from '../hooks/useSimplifiedTranscribe'
import useDataExtraction from '../hooks/useDataExtraction'
import RecordingStatusBar from './RecordingStatusBar'
import ContentDisplay from './ContentDisplay'
import DataExtractionModal from './DataExtractionModal'
import WorkArea from './WorkArea'
import LanguageSelector from './LanguageSelector'
import './transcribeai.css'

const { Title } = Typography

const SimplifiedTranscribeModal = ({
  visible,
  onCancel,
  setNoteState,
  noteState,
  debouncedNoteCapture,
  userRef,
  bookingData,
  websocketConnection,
  onRefresh
}) => {
  const { token } = theme.useToken()
  const { agiliteUser } = useSelector((state) => state.auth)

  const {
    isRecording,
    isPaused,
    recordingTime,
    isProcessing,
    isTyping,
    generatedNote,
    rawAiResponse,
    error,
    audioLevels,
    originalTranscript,
    selectedLanguage,
    handleStartRecording,
    handlePauseRecording,
    handleResumeRecording,
    handleStopRecording,
    handleInsertToNote,
    handleSaveAsTemplate,
    handleRetry,
    handleTypingComplete,
    handleContentChange,
    handleRegenerate,
    resetWorkspace,
    setSelectedLanguage
  } = useSimplifiedTranscribe({ setNoteState, noteState, debouncedNoteCapture, bookingData })

  const {
    isExtracting,
    extractedVitals,
    selectedVitals,
    isModalOpen,
    isSubmitting,
    extractVitalsFromNote,
    toggleVitalSelection,
    submitSelectedVitals,
    closeModal
  } = useDataExtraction({
    userRef,
    bookingData,
    agiliteUser,
    websocketConnection,
    onRefresh
  })

  const handleClose = () => {
    if (isRecording) {
      handleStopRecording()
    }
    // Don't reset workspace - preserve data when modal closes
    onCancel()
  }

  const handleDataExtraction = () => {
    extractVitalsFromNote(generatedNote)
  }

  return (
    <>
      <Modal
        title={
          <Space>
            <FontAwesomeIcon icon={faEarListen} style={{ color: token.colorPrimary }} />
            <Title level={4} style={{ margin: 0 }}>
              TranscribeAI Workspace
            </Title>
          </Space>
        }
        open={visible}
        onCancel={handleClose}
        width='100vw'
        style={{
          top: 0,
          paddingBottom: 0,
          maxWidth: 'none'
        }}
        styles={{
          body: {
            height: 'calc(100vh - 55px)',
            padding: '16px',
            overflow: 'auto',
            backgroundColor: '#f5f5f5'
          },
          mask: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)'
          }
        }}
        className='transcribe-modal'
        footer={null}
        maskClosable={false}
        zIndex={1000}
      >
        {/* Language Selection */}
        <div
          style={{
            marginBottom: '12px',
            padding: '12px 16px',
            backgroundColor: '#fafafa',
            borderRadius: '6px',
            border: `1px solid ${token.colorBorder}`
          }}
        >
          <LanguageSelector
            selectedLanguage={selectedLanguage}
            onLanguageChange={setSelectedLanguage}
            disabled={isRecording || isProcessing}
            showLabel={true}
          />
        </div>

        {/* Top Controls Bar */}
        <RecordingStatusBar
          isRecording={isRecording}
          isPaused={isPaused}
          isProcessing={isProcessing}
          isTyping={isTyping}
          recordingTime={recordingTime}
          audioLevels={audioLevels}
          onStartRecording={handleStartRecording}
          onPauseRecording={handlePauseRecording}
          onResumeRecording={handleResumeRecording}
          onStopRecording={handleStopRecording}
        />

        {/* Main Content Area */}
        <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
          {/* Left Panel - Generated Clinical Notes */}
          <Col span={12}>
            <ContentDisplay
              isProcessing={isProcessing}
              isTyping={isTyping}
              generatedNote={generatedNote}
              rawAiResponse={rawAiResponse}
              error={error}
              onRetry={handleRetry}
              onTypingComplete={handleTypingComplete}
              handleRegenerate={handleRegenerate}
              onContentChange={handleContentChange}
              onInsertToNote={handleInsertToNote}
              onClearWorkspace={resetWorkspace}
              onDataExtraction={handleDataExtraction}
              isExtracting={isExtracting}
              bookingData={bookingData}
              userRef={userRef}
            />
          </Col>

          {/* Right Panel - AI Query Interface */}
          <Col span={12}>
            <WorkArea
              originalTranscript={originalTranscript}
              onInsertToNote={handleInsertToNote}
              bookingData={bookingData}
              userRef={userRef}
              selectedLanguage={selectedLanguage}
            />
          </Col>
        </Row>
      </Modal>

      {/* Data Extraction Modal */}
      <DataExtractionModal
        isOpen={isModalOpen}
        onClose={closeModal}
        extractedVitals={extractedVitals}
        selectedVitals={selectedVitals}
        onToggleVital={toggleVitalSelection}
        onSubmit={submitSelectedVitals}
        isSubmitting={isSubmitting}
      />
    </>
  )
}

export default SimplifiedTranscribeModal
